import { Fragment } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Image } from '@opendoor/atoms/image';
import { Link } from '@opendoor/atoms/link';
import { Text } from '@opendoor/atoms/text';

import FooterAppDownload from '../landing-pages-v2/shared/footer/FooterAppDownload';
import FooterBrand from '../landing-pages-v2/shared/footer/FooterBrand';
import FooterHelpCenter from '../landing-pages-v2/shared/footer/FooterHelpCenter';

const APPLE_STORE_LINK =
  'https://itunes.apple.com/us/app/opendoor-homes-all-day-open/id1143708068?mt=8';
const GOOGLE_PLAY_LINK =
  'https://play.google.com/store/apps/details?id=com.opendoor.buyerapp&hl=en';
const HELP_CENTER_LINK = 'https://help.opendoor.com/';
const APPLE_STORE_IMAGE_URL =
  'https://images.opendoor.com/source/s3/imgdrop-production/2023-12-1702502078078-95093.png?service=cosmos';
const GOOGLE_PLAY_STORE_IMAGE_URL =
  'https://images.opendoor.com/source/s3/imgdrop-production/2023-12-1702502078081-28903.png?service=cosmos';
const AI_PROMPT =
  'How does Opendoor work and what is their "Cash Now, More Later" product? I want to understand how Opendoor can help me get a competitive cash offer, sell on my timeline, and potentially earn more through their "Cash Now, More Later" option.';

interface FooterLink {
  text: string;
  href: string;
}

interface FooterColumn {
  title: string;
  links: FooterLink[];
}

interface MarketLink {
  label: string;
  path: string;
}

const footerColumns: FooterColumn[] = [
  {
    title: 'Products',
    links: [
      { text: 'All Cash offer', href: '/how-it-works-sell' },
      { text: 'Cash Now More Later', href: '/cash-now-more-later' },
      { text: 'Sell to buy your next home', href: '/builder' },
      { text: 'Browse homes', href: '/homes' },
      { text: 'Opendoor for Agents', href: '/agents' },
      { text: 'Stories', href: '/stories' },
      { text: 'Reviews', href: '/reviews' },
    ],
  },
  {
    title: 'Partners',
    links: [
      { text: 'Agents', href: '/agents' },
      { text: 'Agent teams', href: '/agent-teams' },
      { text: 'Builders', href: '/partner/builder' },
      { text: 'Business partnerships', href: '/partners' },
      { text: 'Vendors', href: '/vendor-partnerships' },
    ],
  },
  {
    title: 'About',
    links: [
      { text: 'Company', href: '/about' },
      { text: 'News', href: '/newsroom' },
      { text: 'Contact', href: '/contact-us' },
      { text: 'Investors', href: '/investors' },
      { text: 'Careers', href: '/careers' },
      { text: 'Brokerages', href: '/brokerages' },
      { text: 'FAQs', href: '/frequently-asked-questions' },
    ],
  },
  {
    title: 'Terms & Privacy',
    links: [
      { text: 'Trust and safety', href: '/safety' },
      { text: 'Terms of service', href: '/terms' },
      { text: 'Privacy policy', href: '/privacy' },
      { text: 'Notice at collection', href: '/privacy#ccpa' },
      {
        text: 'Do not sell or share my personal information',
        href: 'https://opendoor-privacy.relyance.ai/',
      },
      { text: 'Avoiding rental scams', href: 'https://www.opendoor.com/rental-scam' },
    ],
  },
];

const states: MarketLink[] = [
  { path: 'alabama_other', label: 'Alabama' },
  { path: 'arizona_other', label: 'Arizona' },
  { path: 'arkansas_other', label: 'Arkansas' },
  { path: 'california_other', label: 'California' },
  { path: 'colorado_other', label: 'Colorado' },
  { path: 'connecticut_other', label: 'Connecticut' },
  { path: 'delaware_other', label: 'Delaware' },
  { path: 'florida_other', label: 'Florida' },
  { path: 'georgia_other', label: 'Georgia' },
  { path: 'idaho_other', label: 'Idaho' },
  { path: 'illinois_other', label: 'Illinois' },
  { path: 'indiana_other', label: 'Indiana' },
  { path: 'iowa_other', label: 'Iowa' },
  { path: 'kansas_other', label: 'Kansas' },
  { path: 'kentucky_other', label: 'Kentucky' },
  { path: 'louisiana_other', label: 'Louisiana' },
  { path: 'maine_other', label: 'Maine' },
  { path: 'maryland_other', label: 'Maryland' },
  { path: 'massachusetts_other', label: 'Massachusetts' },
  { path: 'michigan_other', label: 'Michigan' },
  { path: 'minnesota_other', label: 'Minnesota' },
  { path: 'mississippi_other', label: 'Mississippi' },
  { path: 'missouri_other', label: 'Missouri' },
  { path: 'montana_other', label: 'Montana' },
  { path: 'nebraska_other', label: 'Nebraska' },
  { path: 'nevada_other', label: 'Nevada' },
  { path: 'new_hampshire_other', label: 'New Hampshire' },
  { path: 'new_jersey_other', label: 'New Jersey' },
  { path: 'new_mexico_other', label: 'New Mexico' },
  { path: 'new_york_other', label: 'New York' },
  { path: 'north_carolina_other', label: 'North Carolina' },
  { path: 'north_dakota_other', label: 'North Dakota' },
  { path: 'ohio_other', label: 'Ohio' },
  { path: 'oklahoma_other', label: 'Oklahoma' },
  { path: 'oregon_other', label: 'Oregon' },
  { path: 'pennsylvania_other', label: 'Pennsylvania' },
  { path: 'rhode_island_other', label: 'Rhode Island' },
  { path: 'south_carolina_other', label: 'South Carolina' },
  { path: 'south_dakota_other', label: 'South Dakota' },
  { path: 'tennessee_other', label: 'Tennessee' },
  { path: 'texas_other', label: 'Texas' },
  { path: 'utah_other', label: 'Utah' },
  { path: 'vermont_other', label: 'Vermont' },
  { path: 'virginia_other', label: 'Virginia' },
  { path: 'washington_other', label: 'Washington' },
  { path: 'west_virginia_other', label: 'West Virginia' },
  { path: 'wisconsin_other', label: 'Wisconsin' },
  { path: 'wyoming_other', label: 'Wyoming' },
];

const topLocations: MarketLink[] = [
  { path: 'houston', label: 'Houston' },
  { path: 'las_vegas', label: 'Las Vegas' },
  { path: 'san_antonio', label: 'San Antonio' },
  { path: 'dallas', label: 'Dallas' },
  { path: 'san_diego_ca', label: 'San Diego' },
  { path: 'oklahoma_city_ok', label: 'Oklahoma City' },
  { path: 'los_angeles', label: 'Los Angeles' },
  { path: 'jacksonville', label: 'Jacksonville' },
  { path: 'philadelphia_pa', label: 'Philadelphia' },
  { path: 'austin', label: 'Austin' },
  { path: 'raleigh', label: 'Raleigh' },
  { path: 'portland', label: 'Portland' },
  { path: 'sacramento', label: 'Sacramento' },
  { path: 'indianapolis_in', label: 'Indianapolis' },
  { path: 'charlotte', label: 'Charlotte' },
  { path: 'detroit_mi', label: 'Detroit' },
  { path: 'seattle_wa', label: 'Seattle' },
  { path: 'memphis_tn', label: 'Memphis' },
  { path: 'corpus_christi_tx', label: 'Corpus Christi' },
  { path: 'pittsburgh_pa', label: 'Pittsburgh' },
  { path: 'atlanta', label: 'Atlanta' },
  { path: 'phoenix', label: 'Phoenix' },
  { path: 'kansas_city', label: 'Kansas City' },
  { path: 'washington_dc', label: 'Washington, DC' },
  { path: 'miami_fl', label: 'Miami' },
  { path: 'tucson', label: 'Tucson' },
  { path: 'denver', label: 'Denver' },
  { path: 'sf_bay_area', label: 'San Francisco Bay Area' },
  { path: 'orlando', label: 'Orlando' },
  { path: 'minneapolis', label: 'Minneapolis' },
];

const StaticFooterNav = () => {
  return (
    <Col className="min-w-0 flex-1">
      <Row className="flex-wrap justify-between gap-6 md:flex-nowrap">
        {footerColumns.map((column) => (
          <Col key={column.title} className="min-w-0 grow basis-[45%] gap-6">
            <Text as="h3" className="text-subheader-sm">
              {column.title}
            </Text>
            <Col role="list" className="gap-4">
              {column.links.map((link) => (
                <Box role="listitem" key={`${column.title}-${link.text}`}>
                  <Box
                    analyticsName={`footer-link-column-${link.text}`}
                    href={link.href}
                    aria-label={link.text}
                    className="w-max max-w-full text-body-md"
                  >
                    {link.text}
                  </Box>
                </Box>
              ))}
            </Col>
          </Col>
        ))}
      </Row>
    </Col>
  );
};

const FooterMarketLinks = ({ heading, links }: { heading: string; links: MarketLink[] }) => {
  return (
    <Col className="w-full items-start gap-4 overflow-hidden rounded-xl bg-od-bg-inverse-secondary p-4">
      <Text as="p" className="min-w-20 shrink-0 text-label-sm">
        {heading}
      </Text>
      <Row role="list" className="min-w-0 flex-1 flex-wrap items-end gap-2">
        {links.map((market, i) => (
          <Fragment key={market.path}>
            <Box role="listitem">
              <Box
                analyticsName={`footer-link-column-${market.label}`}
                href={`/sell/${market.path}`}
                aria-label={market.label}
                className="w-max text-body-md"
              >
                {market.label}
              </Box>
            </Box>
            {i < links.length - 1 && (
              <Text as="span" aria-hidden="true">
                |
              </Text>
            )}
          </Fragment>
        ))}
      </Row>
    </Col>
  );
};

const StaticFooterLegal = () => {
  return (
    <Col className="gap-4 rounded-xl bg-od-bg-inverse-secondary p-4">
      <Text as="p" className="text-body-md">
        CA: Opendoor Brokerage Inc. DRE #02061130
      </Text>
      <Text as="p" className="text-body-md">
        NY:{' '}
        <Link
          analyticsName="cosmos-landing-page-v2-footer-legal-link"
          aria-label="Licensed as Opendoor Brokerage LLC pdf link"
          href="https://odfil.es/static/documents/Dan+NY+Notarized+Doc.pdf"
          className="!text-od-content-inverse-primary"
        >
          Licensed as Opendoor Brokerage LLC
        </Link>
        ,{' '}
        <Link
          analyticsName="cosmos-landing-page-v2-footer-legal-link"
          aria-label="Fair Housing Notice pdf link"
          href="https://odfil.es/static/documents/fair-housing-notice.pdf"
          className="!text-od-content-inverse-primary"
        >
          Fair Housing Notice
        </Link>
      </Text>
      <Text as="p" className="text-body-md">
        TX:{' '}
        <Link
          analyticsName="cosmos-landing-page-v2-footer-legal-link"
          aria-label="TREC Information About Brokerage Services pdf link"
          href="https://odfil.es/static/IABS_Jan_2026.pdf"
          className="!text-od-content-inverse-primary"
        >
          TREC Information About Brokerage Services
        </Link>
        ,{' '}
        <Link
          analyticsName="cosmos-landing-page-v2-footer-legal-link"
          aria-label="TREC Consumer Protection Notice pdf link"
          href="https://www.trec.texas.gov/forms/consumer-protection-notice"
          className="!text-od-content-inverse-primary"
        >
          TREC Consumer Protection Notice
        </Link>
      </Text>
      <Text as="p" className="text-body-md">
        Opendoor Labs Inc. is dedicated to improving the accessibility of its Website and we view
        accessibility as a continually ongoing effort. Please contact us at{' '}
        <Link
          href="mailto:support@opendoor.com"
          aria-label="Contact support link"
          analyticsName="cosmos-contact-opendoor-email"
          className="!text-od-content-inverse-primary"
        >
          support@opendoor.com
        </Link>{' '}
        with any support needs, feedback or suggestions.
      </Text>
    </Col>
  );
};

const StaticSiteFooter = () => {
  return (
    <Col
      as="footer"
      className="min-h-113 shrink-0 bg-od-bg-inverse-primary text-od-content-inverse-primary"
    >
      <Box className="w-full justify-center">
        <Box className="block w-full max-w-360 p-5 pt-22 md:p-20">
          <Col className="max-w-full gap-16 lg:flex-row">
            <FooterBrand footerTitle="The better way to buy and sell" aiPrompt={AI_PROMPT} />
            <Col className="min-w-0 flex-1 gap-4">
              <StaticFooterNav />
              <FooterMarketLinks heading="Explore your state’s housing market" links={states} />
              <FooterMarketLinks heading="Top locations" links={topLocations} />
              <Col className="gap-4 xl:flex-row">
                <FooterAppDownload
                  appleStoreLink={APPLE_STORE_LINK}
                  appleStoreImageUrl={APPLE_STORE_IMAGE_URL}
                  googlePlaystoreLink={GOOGLE_PLAY_LINK}
                  googlePlayStoreImageUrl={GOOGLE_PLAY_STORE_IMAGE_URL}
                />
                <FooterHelpCenter helpCenterLink={HELP_CENTER_LINK} />
              </Col>

              <StaticFooterLegal />

              <Row className="flex-wrap items-center gap-4 pt-16">
                <Text as="span" className="text-body-md">
                  &copy; {new Date().getFullYear()} Opendoor | All rights reserved.
                </Text>
                <Row className="items-center gap-4">
                  <Image
                    src="https://images.opendoor.com/source/s3/imgdrop-production/f2398a1e83a24962a9297d13f2ac6ea5.png?preset=square-2048"
                    alt="Equal Housing Opportunity"
                    width={38}
                    height={38}
                  />
                  <Box
                    href="https://www.bbb.org/us/az/tempe/profile/real-estate-services/opendoor-1126-1000060421/#sealclick"
                    analyticsName="cosmos-landing-page-v2-footer-bbb-link"
                    aria-label="Better Business Bureau accredited since June 8th 2021 link"
                    className="inline-flex"
                  >
                    <Image
                      src="https://images.opendoor.com/source/s3/imgdrop-production/1cd65eee0b8345689ceff0292986f125.png?preset=square-2048"
                      alt="Better Business Bureau"
                      width={105}
                      height={38}
                    />
                  </Box>
                </Row>
              </Row>
            </Col>
          </Col>
        </Box>
      </Box>
    </Col>
  );
};

export default StaticSiteFooter;
